import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import Layout from '../../components/Layout';
import BooksellerListFilters from '../../components/admin/booksellers/BooksellerListFilters';
import BooksellerListTable from '../../components/admin/booksellers/BooksellerListTable';
import BooksellerEditDrawer from '../../components/admin/booksellers/BooksellerEditDrawer';
import BooksellerDeleteConfirmDialog from '../../components/admin/booksellers/BooksellerDeleteConfirmDialog';
import { useBooksellerListQuery } from '../../hooks/useBooksellerListQuery';
import {
  AuthenticationType,
  DEFAULT_BOOKSELLER_FILTER_STATE,
  TtsServiceLevel,
  type BooksellerFilterState,
  type BooksellerManageDto,
} from '../../types/manageBooksellers';

export default function ManageBooksellersPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  const filters = useMemo<BooksellerFilterState>(() => {
    const f: BooksellerFilterState = { ...DEFAULT_BOOKSELLER_FILTER_STATE };
    const page = Number(params.get('page')); if (page > 0) f.page = page;
    const pageSize = Number(params.get('pageSize')); if (pageSize > 0) f.pageSize = pageSize;
    const q = params.get('q'); if (q) f.q = q;
    const isActive = params.get('isActive');
    if (isActive === 'true') f.isActive = true;
    if (isActive === 'false') f.isActive = false;
    const authType = params.get('authType');
    if (authType && !Number.isNaN(Number(authType))) f.authType = Number(authType) as AuthenticationType;
    const tts = params.get('tts');
    if (tts && !Number.isNaN(Number(tts))) f.ttsServiceLevel = Number(tts) as TtsServiceLevel;
    const sort = params.get('sort');
    if (sort === 'name' || sort === 'slug' || sort === 'priority' || sort === 'creationTime') f.sort = sort;
    const dir = params.get('dir');
    if (dir === 'asc' || dir === 'desc') f.dir = dir;
    return f;
  }, [params]);

  const [editMode, setEditMode] = useState<'new' | string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<BooksellerManageDto | null>(null);

  const writeFiltersToUrl = (next: BooksellerFilterState) => {
    const out = new URLSearchParams();
    const set = (k: string, v: unknown) => {
      if (v === undefined || v === null || v === '' || v === false) return;
      out.set(k, String(v));
    };
    set('page', next.page);
    set('pageSize', next.pageSize);
    set('q', next.q);
    set('isActive', next.isActive);
    set('authType', next.authType);
    set('tts', next.ttsServiceLevel);
    set('sort', next.sort);
    set('dir', next.dir);
    setParams(out);
  };

  const updateFilters = (patch: Partial<BooksellerFilterState>) => {
    writeFiltersToUrl({ ...filters, ...patch });
  };

  const { data, isLoading, error } = useBooksellerListQuery(filters);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold">{t('App.Admin.Booksellers.ManageBooksellersPage.Title')}</h1>
          <button
            type="button"
            onClick={() => setEditMode('new')}
            className="px-3 py-2 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 min-h-[44px]"
          >
            {t('App.Admin.Booksellers.ManageBooksellersPage.NewBookseller')}
          </button>
        </div>

        <BooksellerListFilters filters={filters} onChange={updateFilters} />

        {isLoading && <p>{t('App.Admin.Booksellers.ManageBooksellersPage.Loading')}</p>}
        {error && (
          <p className="text-red-600">{t('App.Admin.Booksellers.ManageBooksellersPage.LoadError', { error: String((error as Error)?.message ?? error) })}</p>
        )}

        {data && (
          <BooksellerListTable
            rows={data.items}
            totalCount={data.totalCount}
            filters={filters}
            onChange={updateFilters}
            onEdit={(id) => setEditMode(id)}
            onDelete={(row) => setPendingDelete(row)}
          />
        )}
      </div>

      <BooksellerEditDrawer mode={editMode} onClose={() => setEditMode(null)} />

      <BooksellerDeleteConfirmDialog
        bookseller={pendingDelete}
        onClose={() => setPendingDelete(null)}
      />
    </Layout>
  );
}
